import { isRight } from 'fp-ts/Either';
import { Loader2, PackageSearch, Plus, Search } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useS3Image } from '../../hooks/useS3Image';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';

export type CatalogDapp = {
  id: string;
  dappName: string;
  packageName: string | null;
  subtitle?: string | null;
  dappIconUrl: string | null;
  dappIconThumbnailUrl?: string | null;
  status: string;
  publisherName: string;
};

export function dappPrefillPatch(dapp: CatalogDapp): Record<string, string> {
  const patch: Record<string, string> = {
    appName: dapp.dappName,
    androidPackage: dapp.packageName ?? '',
  };

  // Prefer the CDN thumbnail over the raw (often private) S3 object.
  const iconUrl = dapp.dappIconThumbnailUrl ?? dapp.dappIconUrl;
  if (iconUrl) {
    patch.iconUrl = iconUrl;
  }
  if (dapp.subtitle?.trim()) {
    patch.curationNote = dapp.subtitle.trim();
  }

  return patch;
}

export function DappIcon(props: {
  url: string | null | undefined;
  name: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  // Internal dev-portal S3 icon URLs are private and must be exchanged for a
  // presigned URL; external/CDN URLs (and already-signed ones) pass through.
  const { imageUrl, isLoading, error } = useS3Image(props.url);

  // Re-attempt when the URL changes so a recycled instance (e.g. switching apps
  // in the tree) doesn't stay stuck on a previous failure.
  useEffect(() => {
    setFailed(false);
  }, [props.url]);

  if (!props.url || failed || error) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'flex shrink-0 items-center justify-center bg-neutral-800 text-[0.6em] font-semibold uppercase text-neutral-400',
          props.className,
        )}
      >
        {props.name.trim().charAt(0) || '?'}
      </span>
    );
  }

  if (!imageUrl) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'shrink-0 bg-neutral-800',
          isLoading && 'animate-pulse',
          props.className,
        )}
      />
    );
  }

  return (
    <img
      src={imageUrl}
      alt=""
      onError={() => setFailed(true)}
      className={cn('shrink-0 bg-neutral-800 object-cover', props.className)}
    />
  );
}

function ResultSkeleton() {
  return (
    <div className="space-y-1.5" aria-hidden="true">
      {[0, 1, 2].map((row) => (
        <div
          key={row}
          className="flex animate-pulse items-center gap-3 rounded-lg border border-neutral-800/60 bg-neutral-900/40 px-3 py-2.5"
        >
          <span className="size-8 rounded-lg bg-neutral-800" />
          <span className="flex-1 space-y-1.5">
            <span className="block h-2.5 w-1/3 rounded bg-neutral-800" />
            <span className="block h-2 w-1/2 rounded bg-neutral-800/70" />
          </span>
        </div>
      ))}
    </div>
  );
}

export function DappPickerDialog(props: {
  title: string;
  description: string;
  blankLabel?: string;
  onBlank?: () => void;
  onClose: () => void;
  onPick: (prefill: Record<string, string>) => void;
}) {
  const trpc = useTRPC();
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const catalog = useAsyncData<CatalogDapp[] | null>({ initialData: null });
  const { run } = catalog;

  useEffect(() => {
    const timer = setTimeout(
      () => {
        void run(
          () =>
            trpc.listAllDapps.query({
              filters: { search: search.trim() || undefined },
              pagination: { page: 1, limit: 8 },
            }),
          {
            onSuccess: (result, { setData, setError }) => {
              if (isRight(result)) {
                setData(result.right.dapps);
                return;
              }
              setError('Could not load the dapp catalog.');
            },
            onError: (_error, { setError }) =>
              setError('Could not load the dapp catalog.'),
          },
        );
      },
      search ? 250 : 0,
    );

    return () => clearTimeout(timer);
  }, [search, run, trpc]);

  // Reset the highlight only when the query changes, not on every refetch.
  useEffect(() => {
    setHighlighted(0);
  }, [search]);

  const results = catalog.data ?? [];

  // Keep the highlight in range when a debounced refetch returns fewer rows.
  useEffect(() => {
    setHighlighted((current) => (current >= results.length ? 0 : current));
  }, [results.length]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-result-index="${highlighted}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((current) =>
        Math.min(current + 1, Math.max(results.length - 1, 0)),
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === 'Enter' && results[highlighted]) {
      event.preventDefault();
      props.onPick(dappPrefillPatch(results[highlighted]));
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          props.onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg gap-0 overflow-hidden p-0">
        <div className="border-b border-neutral-800 px-4 py-3.5 pr-12">
          <DialogTitle className="text-sm font-semibold">
            {props.title}
          </DialogTitle>
          <DialogDescription className="mt-0.5 text-xs">
            {props.description}
          </DialogDescription>
        </div>

        <div className="border-b border-neutral-800 p-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-neutral-500"
              aria-hidden="true"
            />
            <input
              type="search"
              aria-controls="dapp-picker-results"
              aria-label="Search dapp catalog"
              placeholder="Search dapps by name or package…"
              autoFocus
              value={search}
              onInput={(event) => setSearch(event.currentTarget.value)}
              onKeyDown={onSearchKeyDown}
              className="h-10 w-full rounded-lg border border-neutral-800 bg-neutral-950/60 pl-9 pr-9 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors focus:border-teal-500/70 focus:outline-none focus:ring-2 focus:ring-teal-500/15"
            />
            {catalog.isLoading && (
              <Loader2 className="absolute right-3 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-neutral-500" />
            )}
          </div>
        </div>

        <div
          ref={listRef}
          id="dapp-picker-results"
          role="listbox"
          aria-label="Dapp catalog results"
          className="max-h-80 min-h-[8.5rem] overflow-y-auto p-2"
        >
          {catalog.error && (
            <p className="px-2 py-3 text-center text-xs text-red-300">
              {catalog.error}
            </p>
          )}
          {!catalog.error && catalog.data === null && <ResultSkeleton />}
          {!catalog.error && catalog.data !== null && results.length === 0 && (
            <div className="flex flex-col items-center gap-2 px-2 py-6 text-center">
              <PackageSearch className="size-5 text-neutral-600" />
              <p className="text-xs text-neutral-400">
                No dapps matched your search.
              </p>
            </div>
          )}
          {!catalog.error && results.length > 0 && (
            <div className="space-y-1">
              {results.map((dapp, index) => (
                <button
                  key={dapp.id}
                  type="button"
                  role="option"
                  aria-selected={index === highlighted}
                  data-result-index={index}
                  onClick={() => props.onPick(dappPrefillPatch(dapp))}
                  onMouseEnter={() => setHighlighted(index)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                    index === highlighted
                      ? 'border-teal-500/40 bg-teal-500/5'
                      : 'border-transparent hover:border-neutral-800 hover:bg-neutral-800/40',
                  )}
                >
                  <DappIcon
                    url={dapp.dappIconThumbnailUrl ?? dapp.dappIconUrl}
                    name={dapp.dappName}
                    className="size-8 rounded-lg text-sm"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-neutral-100">
                      {dapp.dappName}
                    </span>
                    <span className="block truncate font-mono text-xs text-neutral-500">
                      {dapp.packageName ?? 'No package name'}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-[11px] text-neutral-400">
                      {dapp.status}
                    </span>
                    <span className="block max-w-28 truncate text-[11px] text-neutral-500">
                      {dapp.publisherName}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {props.onBlank && (
          <div className="border-t border-neutral-800 p-2">
            <button
              type="button"
              onClick={props.onBlank}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-300 transition-colors hover:bg-neutral-800/60 hover:text-white"
            >
              <Plus className="size-4" />
              {props.blankLabel ?? 'Start with a blank app'}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
